import { useEffect, useState } from 'react'
import { Link, useLocation } from 'wouter'
import { BackdropOrbs } from '../components/BackdropOrbs'
import { SiteFooter } from '../components/SiteFooter'
import {
  CharacterForm,
  formValuesFrom,
  type CharacterFormValues,
} from '../components/CharacterForm'
import { ApiError } from '../lib/api'
import {
  deleteCharacter,
  fetchCharacter,
  getManageKey,
  removeManageKey,
  requestManageKey,
  updateCharacter,
  type PublicCharacter,
} from '../lib/characters'

/** Edit an existing character. Requires the manage key: the stored one when
 *  available, otherwise the user is asked to paste it (and it is verified
 *  against the API before anything is saved). */

export function CharacterEdit({ id }: { id: string }) {
  const [, setLocation] = useLocation()
  const [char, setChar] = useState<PublicCharacter | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [needsKey, setNeedsKey] = useState(false)

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setChar(null)
    setLoadError(null)
    fetchCharacter(id)
      .then((res) => {
        if (!alive) return
        setChar(res.character)
        if (!getManageKey(res.character.id)) setNeedsKey(true)
      })
      .catch(() => {
        if (alive) setLoadError('Could not load this character. It may have been deleted.')
      })
    return () => {
      alive = false
    }
  }, [id])

  const enterKey = async () => {
    const key = await requestManageKey(id)
    if (key) setNeedsKey(false)
  }

  const save = async (values: CharacterFormValues) => {
    const key = getManageKey(id) ?? (await requestManageKey(id))
    if (!key) return

    setSubmitting(true)
    setError(null)
    try {
      const res = await updateCharacter(id, key, values)
      setLocation(`/characters/${res.character.id}`)
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        // stored key went stale (or a wrong one was pasted): drop it so the
        // next attempt asks again
        removeManageKey(id)
        setError('That manage key does not match this character. Try saving again to re-enter it.')
      } else {
        setError(
          err instanceof ApiError
            ? err.message
            : 'Something went wrong while saving. Try again.',
        )
      }
      setSubmitting(false)
    }
  }

  const onDelete = async () => {
    if (!char || deleting) return
    if (!window.confirm(`Delete "${char.title}" permanently?`)) return

    const key = getManageKey(id) ?? (await requestManageKey(id))
    if (!key) return

    setDeleting(true)
    setDeleteError(null)
    try {
      await deleteCharacter(id, key)
      removeManageKey(id)
      setLocation('/characters')
    } catch (err) {
      setDeleting(false)
      setDeleteError(
        err instanceof ApiError ? err.message : 'Could not delete right now. Try again.',
      )
    }
  }

  return (
    <div className="lorebook-root flex min-h-screen flex-col">
      <BackdropOrbs />
      <header className="sticky top-0 z-30 border-b border-edge bg-void/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 lg:px-6">
          <div className="flex items-center gap-3">
            <Link
              href={char ? `/characters/${char.id}` : '/characters'}
              className="text-sm text-slate-300 transition hover:text-signal"
            >
              ← {char ? char.title : 'Characters'}
            </Link>
            <span className="text-slate-700">/</span>
            <span className="font-display text-sm text-white">Edit</span>
          </div>
        </div>
      </header>

      <div className="mx-auto w-full max-w-2xl flex-1 px-4 py-10 lg:px-6">
        {loadError && (
          <div className="rounded-lg border border-sos/30 bg-sos/5 p-8 text-center text-sm text-slate-300">
            {loadError}
            <div className="mt-4">
              <Link href="/characters" className="text-signal hover:underline">
                Back to the gallery
              </Link>
            </div>
          </div>
        )}

        {!loadError && char === null && (
          <div className="animate-pulse rounded-lg border border-edge bg-void-2/60 p-8">
            <div className="h-6 w-1/3 rounded bg-void" />
            <div className="mt-4 h-3 w-2/3 rounded bg-void" />
          </div>
        )}

        {!loadError && char !== null && (
          <>
            <div className="mb-8">
              <div className="text-[11px] uppercase tracking-[0.35em] text-signal/70">
                Edit character
              </div>
              <h1 className="mt-2 font-display text-4xl font-semibold text-white">{char.title}</h1>
            </div>

            {needsKey ? (
              <div className="rounded-lg border border-edge bg-void-2/60 p-8 text-center">
                <div className="font-display text-lg text-white">Manage key needed</div>
                <p className="mx-auto mt-2 max-w-md text-sm text-slate-400">
                  This browser doesn't hold the manage key for this character. Paste the key you
                  received when it was created to unlock editing.
                </p>
                <button
                  type="button"
                  onClick={enterKey}
                  className="mt-5 rounded-full border border-signal/50 bg-signal/10 px-5 py-2 text-sm text-signal transition hover:bg-signal/20 hover:text-white"
                >
                  Enter manage key
                </button>
              </div>
            ) : (
              <>
                <CharacterForm
                  initial={formValuesFrom(char)}
                  submitting={submitting}
                  serverError={error}
                  submitLabel="Save changes"
                  onSubmit={save}
                />

                <div className="mt-10 rounded-lg border border-sos/20 bg-sos/5 p-5">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-sos/80">
                    Danger zone
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-4">
                    <p className="text-xs text-slate-500">
                      Permanently removes this character from the archive.
                    </p>
                    <button
                      type="button"
                      onClick={onDelete}
                      disabled={deleting}
                      className="shrink-0 rounded-full border border-sos/40 px-4 py-1.5 text-xs text-sos transition hover:bg-sos/10 disabled:opacity-50"
                    >
                      {deleting ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                  {deleteError && <p className="mt-2 text-xs text-sos">{deleteError}</p>}
                </div>
              </>
            )}
          </>
        )}
      </div>
      <SiteFooter />
    </div>
  )
}
